import { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@modules/auth/hooks/useAuth";
import { queryClient } from "@core/libs/react-query/react-queryClient";
import { fetchUser } from "@core/libs/axios/services/authRequests";
import { Loading } from "@core/components";

/**
 * AuthGuard:
 * - If we don't know the user yet, tries once to fetch /user/me (cookie-based).
 * - While checking, shows a loading screen.
 * - If still unauthenticated => redirect to "/".
 * - If authenticated => renders nested routes via <Outlet/>.
 */
const AuthGuard = () => {
  const { isAuthenticated, isLoadingUser } = useAuth();
  const [checking, setChecking] = useState(false);
  const triedRef = useRef(false);
  const location = useLocation();

  useEffect(() => {
    const tryFetchOnce = async () => {
      if (!isAuthenticated && !isLoadingUser && !triedRef.current) {
        triedRef.current = true;
        setChecking(true);
        try {
          // triggers your fetchUser() via react-query using the existing key
          await queryClient.fetchQuery({
            queryKey: ["user"],
            queryFn: () => fetchUser(),
          });
        } finally {
          setChecking(false);
        }
      }
    };
    tryFetchOnce();
  }, [isAuthenticated, isLoadingUser]);

  if (isLoadingUser || checking) return <Loading isLoading />;

  if (!isAuthenticated) {
    // keep "from" to support post-login redirect later
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return <Outlet />;
};

export default AuthGuard;
